import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import type { EquipmentInstance, EquipmentSlot, Rarity } from '@/domain/types';
import { getItem } from '@/domain/items';
import { loadProfile, lockSync, unlockSync } from '@/state/profileSync';
import { useGameStore } from '@/state/gameStore';
import { useAuthStore } from '@/state/authStore';

export interface Listing {
  id: string;
  sellerId: string;
  sellerName: string;
  instanceId: string;
  itemId: string;
  upgradeLevel: number;
  rarity: Rarity;
  price: number;
  status: 'open' | 'sold' | 'cancelled' | 'expired';
  createdAt: number;
  expiresAt: number;
}

export type ListingSort = 'newest' | 'price_asc' | 'price_desc';

interface MarketState {
  listings: Listing[];
  myListings: Listing[];
  loading: boolean;
  error: string | null;

  // Filters (only for browsing tab)
  filterRarity: Rarity | null;
  filterSlot: EquipmentSlot | null;
  sort: ListingSort;

  loadListings: () => Promise<void>;
  loadMyListings: () => Promise<void>;
  createListing: (instance: EquipmentInstance, price: number) => Promise<{ ok: boolean; error?: string }>;
  cancelListing: (listingId: string) => Promise<{ ok: boolean; error?: string }>;
  buyListing: (listingId: string) => Promise<{ ok: boolean; error?: string }>;

  setFilterRarity: (r: Rarity | null) => void;
  setFilterSlot: (s: EquipmentSlot | null) => void;
  setSort: (s: ListingSort) => void;

  /** Subscribe to realtime changes — returns unsubscribe fn. */
  subscribeRealtime: () => () => void;
}

interface ListingViewRow {
  id: string;
  seller_id: string;
  seller_name: string;
  instance_id: string;
  item_id: string;
  upgrade_level: number;
  rarity: string;
  price: number;
  status: string;
  created_at: string;
  expires_at: string;
}

function rowToListing(row: ListingViewRow): Listing {
  return {
    id: row.id,
    sellerId: row.seller_id,
    sellerName: row.seller_name ?? '?',
    instanceId: row.instance_id,
    itemId: row.item_id,
    upgradeLevel: row.upgrade_level,
    rarity: row.rarity as Rarity,
    price: row.price,
    status: row.status as Listing['status'],
    createdAt: new Date(row.created_at).getTime(),
    expiresAt: new Date(row.expires_at).getTime(),
  };
}

function applyFilters(
  list: Listing[],
  rarity: Rarity | null,
  slot: EquipmentSlot | null,
  sort: ListingSort,
): Listing[] {
  let out = list;
  if (rarity) out = out.filter((l) => l.rarity === rarity);
  if (slot) out = out.filter((l) => getItem(l.itemId)?.equipSlot === slot);
  switch (sort) {
    case 'price_asc':
      out = [...out].sort((a, b) => a.price - b.price);
      break;
    case 'price_desc':
      out = [...out].sort((a, b) => b.price - a.price);
      break;
    case 'newest':
    default:
      out = [...out].sort((a, b) => b.createdAt - a.createdAt);
      break;
  }
  return out;
}

async function refreshLocalProfile(): Promise<void> {
  const userId = useAuthStore.getState().user?.id;
  if (!userId) return;
  try {
    const fresh = await loadProfile(userId);
    if (fresh) useGameStore.setState({ player: fresh });
  } catch (err) {
    console.error('[marketStore] refreshLocalProfile failed', err);
  }
}

export const useMarketStore = create<MarketState>()((set, get) => ({
  listings: [],
  myListings: [],
  loading: false,
  error: null,
  filterRarity: null,
  filterSlot: null,
  sort: 'newest',

  setFilterRarity: (r) => set({ filterRarity: r }),
  setFilterSlot: (s) => set({ filterSlot: s }),
  setSort: (s) => set({ sort: s }),

  loadListings: async () => {
    set({ loading: true, error: null });
    const { data, error } = await supabase
      .from('listings_view')
      .select('*')
      .eq('status', 'open')
      .order('created_at', { ascending: false })
      .limit(200);
    if (error) {
      set({ loading: false, error: error.message });
      return;
    }
    const listings = (data as ListingViewRow[]).map(rowToListing);
    set({ listings, loading: false });
  },

  loadMyListings: async () => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId) return;
    const { data, error } = await supabase
      .from('listings_view')
      .select('*')
      .eq('seller_id', userId)
      .order('created_at', { ascending: false })
      .limit(100);
    if (error) {
      set({ error: error.message });
      return;
    }
    set({ myListings: (data as ListingViewRow[]).map(rowToListing) });
  },

  createListing: async (instance, price) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId) return { ok: false, error: 'not_authenticated' };
    const def = getItem(instance.itemId);
    if (!def) return { ok: false, error: 'unknown_item' };

    const { error } = await supabase.from('listings').insert({
      seller_id: userId,
      instance_id: instance.id,
      item_id: instance.itemId,
      upgrade_level: instance.upgradeLevel,
      rarity: def.rarity,
      price,
    });
    if (error) return { ok: false, error: error.message };

    await Promise.all([get().loadListings(), get().loadMyListings()]);
    return { ok: true };
  },

  cancelListing: async (listingId) => {
    const { data, error } = await supabase.rpc('cancel_listing', { p_listing_id: listingId });
    if (error) return { ok: false, error: error.message };
    const result = data as { ok?: boolean; error?: string };
    if (!result?.ok) return { ok: false, error: result?.error ?? 'unknown' };
    await Promise.all([get().loadListings(), get().loadMyListings()]);
    return { ok: true };
  },

  buyListing: async (listingId) => {
    lockSync(); // prevent profileSync from racing & overwriting server changes
    try {
      const { data, error } = await supabase.rpc('buy_listing', { p_listing_id: listingId });
      if (error) return { ok: false, error: error.message };
      const result = data as { ok?: boolean; error?: string };
      if (!result?.ok) return { ok: false, error: result?.error ?? 'unknown' };
      // Server already updated credits + transferred item. Reload local profile to mirror.
      // S-5: catch refresh failures so the user gets a clear error instead of
      // silently stale state. The server-side purchase is already committed,
      // so we still return ok=true — but tag it so the UI can warn.
      try {
        await refreshLocalProfile();
        await get().loadListings();
      } catch (refreshErr) {
        console.error('[marketStore] post-buy refresh failed', refreshErr);
        return { ok: true, error: 'Kauf erfolgreich, aber lokaler Sync fehlgeschlagen — bitte App neu laden.' };
      }
      return { ok: true };
    } finally {
      unlockSync();
    }
  },

  subscribeRealtime: () => {
    const channel = supabase
      .channel('listings_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'listings' }, () => {
        // On any change: refresh both lists. Cheap for small market scale.
        void get().loadListings();
        void get().loadMyListings();
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  },
}));

/** Helper for UI: filtered + sorted listings. */
export function useFilteredListings(): Listing[] {
  const all = useMarketStore((s) => s.listings);
  const rarity = useMarketStore((s) => s.filterRarity);
  const slot = useMarketStore((s) => s.filterSlot);
  const sort = useMarketStore((s) => s.sort);
  return applyFilters(all, rarity, slot, sort);
}
